const AUTHENTICATED_USER_ID_KEY = "dolphin.authenticated-user-id.v1";

const isValidUserId = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value > 0;

const getStorage = (): Storage | null => {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
};

export const rememberAuthenticatedUserId = (userId: number): void => {
  if (!isValidUserId(userId)) return;
  try {
    getStorage()?.setItem(AUTHENTICATED_USER_ID_KEY, String(userId));
  } catch {
    // Authentication must keep working when browser storage is unavailable.
  }
};

export const getRememberedAuthenticatedUserId = (): number | null => {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const value = storage.getItem(AUTHENTICATED_USER_ID_KEY);
    if (value === null) return null;
    const userId = Number(value);
    if (isValidUserId(userId) && String(userId) === value) return userId;
    storage.removeItem(AUTHENTICATED_USER_ID_KEY);
  } catch {
    // Treat inaccessible or malformed storage as an unknown identity.
  }
  return null;
};

export const forgetAuthenticatedUserId = (): void => {
  try {
    getStorage()?.removeItem(AUTHENTICATED_USER_ID_KEY);
  } catch {
    // The in-memory logout path must not be blocked by browser storage.
  }
};

export const resolveAuthenticatedUserId = (
  currentUser: { id?: unknown } | null | undefined,
): number | null => {
  if (isValidUserId(currentUser?.id)) {
    rememberAuthenticatedUserId(currentUser.id);
    return currentUser.id;
  }
  return getRememberedAuthenticatedUserId();
};
